<template>
  <div v-if="mostrar" class="modal-overlay">
    <div class="modal-container">
      <!-- Cabeçalho -->
      <div class="modal-header">
        <h6>{{ modalTitle }}</h6>

        <i
          class="bi bi-x-circle close-icon"
          role="button"
          title="Fechar"
          @click="fecharModal"
        ></i>
      </div>

      <!-- Conteúdo -->
      <div class="modal-content">
        <!-- Aluno -->
        <div class="row mb-2">
          <div class="col">
            <label>Aluno</label>

            <input
              type="text"
              :value="aluno"
              class="form-control"
              disabled
            />
          </div>
        </div>

        <!-- Registro original -->
        <div
          v-if="revisandoFalta"
          class="registro-original mb-3"
        >
          <div class="registro-original-titulo">
            Registro original
          </div>

          <div class="registro-original-texto">
            Falta avisada para o dia
            <strong>{{ formatarDataBr(dataOriginalFalta) }}</strong>

            <template v-if="observacao?.observacao">
              — {{ observacao.observacao }}
            </template>
          </div>

          <small class="text-muted">
            Este registro será mantido no histórico e não será alterado.
          </small>
        </div>

        <!-- Observação -->
        <div class="form-group mb-2">
          <label for="observacao">
            {{
              revisandoFalta
                ? "Motivo ou observação complementar"
                : "Observação"
            }}

            <span
              v-if="!revisandoFalta"
              class="required"
            >
              *
            </span>
          </label>

          <textarea
            id="observacao"
            v-model="form.observacao"
            class="form-control"
            rows="3"
            :required="!revisandoFalta"
            :placeholder="
              revisandoFalta
                ? 'Opcional. Informe o motivo do cancelamento ou da alteração.'
                : ''
            "
          ></textarea>
        </div>

        <!-- Falta avisada -->
        <div class="form-group mb-2 checkbox-container">
          <span>Falta avisada</span>

          <input
            v-model="form.falta_avisada"
            type="checkbox"
            class="small-checkbox"
          />
        </div>

        <small
          v-if="revisandoFalta"
          class="text-muted d-block mb-2"
        >
          Para cancelar a falta, desmarque a opção. Para alterar a data,
          mantenha marcada e informe a nova data.
        </small>

        <!-- Datas -->
        <div
          v-if="form.falta_avisada"
          class="form-group mb-2"
        >
          <label>
            {{
              revisandoFalta
                ? "Nova data da falta"
                : "Data(s) da falta"
            }}

            <span class="required">*</span>
          </label>

          <div
            v-for="(dt, idx) in form.datas_faltas"
            :key="idx"
            class="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="date"
              class="form-control"
              :value="form.datas_faltas[idx]"
              required
              @input="atualizarData(idx, $event)"
              @change="atualizarData(idx, $event)"
              @blur="atualizarData(idx, $event)"
            />

            <!-- Inclusão: permite adicionar várias datas -->
            <button
              v-if="!revisandoFalta && idx === 0"
              type="button"
              class="btn btn-sm btn-outline-primary"
              title="Adicionar outra data"
              @click="adicionarDataFalta"
            >
              <i class="bi bi-plus-lg"></i>
            </button>

            <button
              v-if="!revisandoFalta && idx > 0"
              type="button"
              class="btn btn-sm btn-outline-danger"
              title="Remover esta data"
              @click="removerDataFalta(idx)"
            >
              <i class="bi bi-dash-lg"></i>
            </button>
          </div>

          <small
            v-if="datasDuplicadas"
            class="text-muted"
          >
            Existem datas repetidas — remova as duplicadas.
          </small>

          <small
            v-if="
              revisandoFalta &&
              form.falta_avisada &&
              novaDataIgualOriginal
            "
            class="text-warning d-block mt-1"
          >
            Informe uma data diferente da falta original.
          </small>
        </div>

        <!-- Erro -->
        <div
          v-if="submitError"
          class="alert alert-danger py-2 mt-2"
        >
          {{ submitError }}
        </div>

        <!-- Ações -->
        <div class="row mt-3">
          <div class="col text-end">
            <button
              type="button"
              class="btn btn-secondary me-2"
              :disabled="isSubmitting"
              @click="fecharModal"
            >
              Cancelar
            </button>

            <button
              type="button"
              :class="[
                'btn',
                isSubmitting ? 'btn-success' : 'btn-primary'
              ]"
              :disabled="!formIsValid || isSubmitting"
              @click="salvar"
            >
              {{ isSubmitting ? "Salvando…" : "Salvar" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/services/axios";

export default {
  name: "IncluirObservacao",

  props: {
    mostrar: {
      type: Boolean,
      default: false
    },

    matriculaId: {
      type: Number,
      required: true
    },

    aluno: {
      type: String,
      default: ""
    },

    alunoId: {
      type: [Number, String],
      required: true
    },

    nomeUsuario: {
      type: String,
      default: ""
    },

    modo: {
      type: String,
      default: "inclusao"
    },

    observacao: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      form: {
        observacao: "",
        falta_avisada: false,
        datas_faltas: [""]
      },

      dataOriginalFalta: "",
      isSubmitting: false,
      isResettingForm: false,
      submitError: ""
    };
  },

  computed: {
    revisandoFalta() {
      return (
        this.modo === "edicao" &&
        this.observacao &&
        this.observacao.id &&
        this.normalizarBooleano(this.observacao.falta_avisada)
      );
    },

    modalTitle() {
      return this.revisandoFalta
        ? "Cancelar ou alterar falta avisada"
        : "Incluir Observação";
    },

    datasDuplicadas() {
      const datas = (this.form.datas_faltas || [])
        .map((data) => String(data || "").trim())
        .filter(Boolean);

      return new Set(datas).size !== datas.length;
    },

    novaDataIgualOriginal() {
      if (!this.revisandoFalta || !this.form.falta_avisada) {
        return false;
      }

      const datas = this.getDatasNormalizadas();
      const novaData = datas[0] || "";

      return (
        Boolean(novaData) &&
        novaData === this.dataOriginalFalta
      );
    },

    formIsValid() {
      /*
       * Na revisão de uma falta:
       *
       * - desmarcado = cancelamento, pode salvar;
       * - marcado = alteração, exige uma nova data diferente.
       */
      if (this.revisandoFalta) {
        if (!this.form.falta_avisada) {
          return true;
        }

        const datas = this.getDatasNormalizadas();

        if (datas.length !== 1) return false;
        if (this.datasDuplicadas) return false;
        if (datas[0] === this.dataOriginalFalta) return false;

        return true;
      }

      // Inclusão normal
      if (!String(this.form.observacao || "").trim()) {
        return false;
      }

      if (this.form.falta_avisada) {
        const datas = this.getDatasNormalizadas();

        if (!datas.length) return false;
        if (this.datasDuplicadas) return false;
      }

      return true;
    }
  },

  methods: {
    normalizarBooleano(value) {
      return (
        value === true ||
        value === 1 ||
        value === "1" ||
        value === "true" ||
        value === "t"
      );
    },

    normalizarDataIso(value) {
      if (!value) return "";

      const texto = String(value).trim();
      const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (!match) return "";

      return `${match[1]}-${match[2]}-${match[3]}`;
    },

    formatarDataBr(dataIso) {
      const data = this.normalizarDataIso(dataIso);

      if (!data) return "—";

      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },

    obterDataHojeIsoLocal() {
      const hoje = new Date();

      const ano = hoje.getFullYear();
      const mes = String(hoje.getMonth() + 1).padStart(2, "0");
      const dia = String(hoje.getDate()).padStart(2, "0");

      return `${ano}-${mes}-${dia}`;
    },

    atualizarData(idx, event) {
      const value = String(event?.target?.value || "").trim();

      this.form.datas_faltas.splice(idx, 1, value);
    },

    getDatasNormalizadas() {
      return (this.form.datas_faltas || [])
        .map((data) => this.normalizarDataIso(data))
        .filter(Boolean);
    },

    resetForm() {
      this.isResettingForm = true;
      this.submitError = "";
      this.dataOriginalFalta = "";

      if (!this.revisandoFalta) {
        this.form.observacao = "";
        this.form.falta_avisada = false;
        this.form.datas_faltas = [""];

        this.$nextTick(() => {
          this.isResettingForm = false;
        });

        return;
      }

      /*
       * No modo de revisão:
       *
       * - o texto original é exibido separadamente;
       * - o textarea começa vazio para receber apenas o complemento;
       * - o checkbox vem marcado;
       * - a data original vem preenchida.
       */
      this.dataOriginalFalta = this.normalizarDataIso(
        this.observacao?.data_falta
      );

      this.form.observacao = "";
      this.form.falta_avisada = true;
      this.form.datas_faltas = [
        this.dataOriginalFalta || ""
      ];

      this.$nextTick(() => {
        this.isResettingForm = false;
      });
    },

    adicionarDataFalta() {
      if (this.revisandoFalta) return;

      this.form.datas_faltas.push("");
    },

    removerDataFalta(idx) {
      if (this.revisandoFalta) return;

      this.form.datas_faltas.splice(idx, 1);

      if (!this.form.datas_faltas.length) {
        this.form.datas_faltas = [""];
      }
    },

    fecharModal() {
      if (this.isSubmitting) return;

      this.$emit("fechar");
    },

    async salvarRevisaoFalta() {
      const observacaoOriginalId = Number(this.observacao.id);
      const dataOriginal = this.dataOriginalFalta;

      if (!dataOriginal) {
        throw new Error(
          "O registro original não possui uma data de falta válida."
        );
      }

      const marcadorOrigem =
        `[[FALTA_ORIGEM_ID:${observacaoOriginalId}]]`;

      const complemento = String(
        this.form.observacao || ""
      ).trim();

      let novaData = null;
      let textoPrincipal = "";

      // CANCELAMENTO
      if (!this.form.falta_avisada) {
        textoPrincipal =
          `Falta avisada para o dia ` +
          `${this.formatarDataBr(dataOriginal)} foi cancelada.`;
      }

      // ALTERAÇÃO DA DATA
      if (this.form.falta_avisada) {
        const datas = this.getDatasNormalizadas();
        novaData = datas[0] || "";

        if (!novaData) {
          throw new Error("Informe a nova data da falta.");
        }

        if (novaData === dataOriginal) {
          throw new Error(
            "A nova data deve ser diferente da data originalmente informada."
          );
        }

        textoPrincipal =
          `Falta avisada para o dia ` +
          `${this.formatarDataBr(dataOriginal)} ` +
          `foi alterada para o dia ` +
          `${this.formatarDataBr(novaData)}.`;
      }

      const textoHistorico = [
        marcadorOrigem,
        textoPrincipal,
        complemento
          ? `Motivo/observação: ${complemento}`
          : null
      ]
        .filter(Boolean)
        .join(" ");

      /*
      * 1. Cria o registro histórico de cancelamento/alteração.
      * Esse registro não representa uma falta ativa.
      */
      const historicoResponse = await axios.post(
        "/operacional/alunos-observacoes",
        {
          matricula_id: this.matriculaId,
          aluno_id: this.alunoId,
          observacao: textoHistorico,
          falta_avisada: false,
          data_falta: null,
          nome_usuario: this.nomeUsuario,
          data_observacao: this.obterDataHojeIsoLocal(),
          chamada_id: null
        }
      );

      let novaFalta = null;

      /*
      * 2. Em caso de alteração, cria uma NOVA falta avisada ativa.
      *
      * Não inclui FALTA_ORIGEM_ID neste registro, porque ele passa
      * a ser uma nova falta independente, que poderá ser posteriormente
      * cancelada ou alterada.
      */
      if (this.form.falta_avisada && novaData) {
        const textoFaltaOriginal = String(
          this.observacao?.observacao || ""
        ).trim();

        const novaFaltaResponse = await axios.post(
          "/operacional/alunos-observacoes",
          {
            matricula_id: this.matriculaId,
            aluno_id: this.alunoId,

            // Mantém o motivo original da ausência
            observacao:
              textoFaltaOriginal ||
              `Falta reagendada de ${this.formatarDataBr(dataOriginal)}`,

            falta_avisada: true,
            data_falta: novaData,
            nome_usuario: this.nomeUsuario,
            data_observacao: this.obterDataHojeIsoLocal(),
            chamada_id: null
          }
        );

        novaFalta = novaFaltaResponse.data;
      }

      return {
        historico: historicoResponse.data,
        nova_falta: novaFalta
      };
    },

    async salvarInclusao() {
      const basePayload = {
        matricula_id: this.matriculaId,
        aluno_id: this.alunoId,
        observacao: String(this.form.observacao || "").trim(),
        falta_avisada: this.form.falta_avisada === true,
        nome_usuario: this.nomeUsuario,
        data_observacao: this.obterDataHojeIsoLocal()
      };

      // Inclusão de uma ou mais faltas
      if (this.form.falta_avisada) {
        const datas = this.getDatasNormalizadas();

        if (!datas.length) {
          throw new Error(
            "Informe ao menos uma data para a falta avisada."
          );
        }

        const response = await axios.post(
          "/operacional/alunos-observacoes/bulk",
          {
            ...basePayload,
            datas_faltas: datas
          }
        );

        return response.data;
      }

      // Observação comum
      const response = await axios.post(
        "/operacional/alunos-observacoes",
        {
          ...basePayload,
          data_falta: null,
          chamada_id: null
        }
      );

      return response.data;
    },

    async salvar() {
      if (this.isSubmitting || !this.formIsValid) return;

      this.isSubmitting = true;
      this.submitError = "";

      try {
        await this.$nextTick();

        const resultado = this.revisandoFalta
          ? await this.salvarRevisaoFalta()
          : await this.salvarInclusao();

        this.$emit("salvou", resultado);
        this.$emit("fechar");
      } catch (error) {
        console.error("Erro ao salvar observação:", error);
        console.error("Detalhe:", error?.response?.data);

        this.submitError =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar a observação.";
      } finally {
        this.isSubmitting = false;
      }
    }
  },

  watch: {
    mostrar: {
      immediate: true,

      handler(newValue) {
        if (newValue) {
          this.resetForm();
        }
      }
    },

    observacao: {
      immediate: true,
      deep: true,

      handler() {
        if (this.mostrar) {
          this.resetForm();
        }
      }
    },

    modo() {
      if (this.mostrar) {
        this.resetForm();
      }
    },

    "form.falta_avisada"(newValue) {
      if (this.isResettingForm) return;

      if (!newValue) {
        this.form.datas_faltas = [""];
        return;
      }

      const primeiraData =
        this.form.datas_faltas?.[0] || "";

      if (!primeiraData) {
        /*
         * Caso o usuário desmarque e marque novamente durante a revisão,
         * restaura a data original.
         */
        this.form.datas_faltas = [
          this.revisandoFalta
            ? this.dataOriginalFalta
            : ""
        ];
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 500px;
  border-radius: 0;
  overflow: hidden;
}

.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-content {
  padding: 15px;
}

.form-group {
  margin-bottom: 1rem;
}

.required {
  color: red;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.small-checkbox {
  width: 16px;
  height: 16px;
}

label {
  font-size: 0.7rem;
  font-weight: 500;
}

.modal-content input[type="date"] {
  max-width: 220px;
}

.btn.btn-sm {
  line-height: 1;
  padding: 6px 8px;
}

.registro-original {
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-left: 3px solid #6c757d;
  border-radius: 4px;
}

.registro-original-titulo {
  margin-bottom: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #495057;
}

.registro-original-texto {
  margin-bottom: 4px;
  font-size: 0.8rem;
  color: #212529;
}
</style>